// PreviewPage.jsx
import * as React from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Stack,
  Chip,
  Button,
  ButtonGroup,
  Avatar,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../utils/constants";

/**
 * Helper: format dates to a friendly, print-safe format
 */
function fmtDate(str) {
  if (!str) return "—";
  const d = new Date(str);
  return isNaN(d)
    ? str
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
}

/**
 * Helper: normalize value display
 */
function NV(v) {
  if (v === null || v === undefined) return "—";
  const s = String(v).trim();
  return s.length ? s : "—";
}

/**
 * Reusable labeled cell pair inside the table
 */
function FieldCells({ label, value }) {
  return (
    <>
      <TableCell
        sx={{
          width: "25%",
          py: 1,
          borderColor: "divider",
          fontWeight: 500,
          wordBreak: "break-word",
        }}
      >
        {label}
      </TableCell>
      <TableCell
        sx={{
          width: "25%",
          py: 1,
          borderColor: "divider",
          fontWeight: 400,
          wordBreak: "break-word",
          overflowWrap: "break-word",
          maxWidth: 0,
        }}
      >
        {value}
      </TableCell>
    </>
  );
}

/**
 * Section header row
 */
function SectionRow({ title }) {
  return (
    <TableRow>
      <TableCell
        colSpan={4}
        sx={{
          py: 1,
          backgroundColor: "rgba(0,0,0,0.03)",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, letterSpacing: 0.3, color: "text.secondary" }}
        >
          {title}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

/**
 * @param {{  onPdfReady?: (base64:string, dataUri:string, blob:Blob)=>void }} props
 */
export default function PreviewPage({ onPdfReady }) {
  const location = useLocation();
  const data = location?.state?.regData;
  const HotelData = location?.state?.HotelData;
  const HomepageData = location?.state?.HomepageData;
  const navigate = useNavigate();

  // NEW: the area to export as PDF
  const printAreaRef = React.useRef(null);
  const [isGenerating, setIsGenerating] = React.useState(false);

  // Helper function to convert image src to data URL
  const toDataUrl = (src) => {
    if (!src) return "";
    if (src.startsWith("http") || src.startsWith("data:")) return src;
    // raw base64 -> guess MIME
    const mime = src.startsWith("/9j/")
      ? "image/jpeg"
      : src.startsWith("iVBOR")
      ? "image/png"
      : "image/*";
    return `data:${mime};base64,${src}`;
  };

  // NEW: create PDF + get Base64 (capture UI as image and convert to PDF)
  const handlePrint = async () => {
    try {
      setIsGenerating(true);
      
      // Import both html2canvas and jsPDF
      const html2canvasModule = await import("html2canvas");
      const html2canvas = html2canvasModule.default || html2canvasModule;
      const { jsPDF } = await import("jspdf");
      
      const filename = `GRC-${data?.registrationNo || "guest"}.pdf`;
      const element = printAreaRef.current;
      
      if (!element) {
        console.error("Print area element not found");
        setIsGenerating(false);
        return;
      }
      
      // Capture the UI as an image using html2canvas
      const canvas = await html2canvas(element, {
        scale: 1.5, // Reduced scale for smaller file size
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        width: element.scrollWidth,
        height: element.scrollHeight,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
      });
      
      // Convert canvas to image with compression
      let imgData = canvas.toDataURL("image/jpeg", 0.85); // JPEG with 85% quality for smaller size
      let imgWidth = canvas.width;
      let imgHeight = canvas.height;
      
      // A4 dimensions in mm
      const A4_WIDTH_MM = 210;
      const A4_HEIGHT_MM = 297;
      const MARGIN_MM = 12;
      const CONTENT_WIDTH_MM = A4_WIDTH_MM - MARGIN_MM * 2;
      const CONTENT_HEIGHT_MM = A4_HEIGHT_MM - MARGIN_MM * 2;
      
      // Calculate aspect ratio
      const imgAspectRatio = imgWidth / imgHeight;
      const contentAspectRatio = CONTENT_WIDTH_MM / CONTENT_HEIGHT_MM;
      
      // Scale to fit content width (no extra margin on right)
      let scaledWidth = CONTENT_WIDTH_MM;
      let scaledHeight = CONTENT_WIDTH_MM / imgAspectRatio;
      
      // If height exceeds page height, scale by height instead
      if (scaledHeight > CONTENT_HEIGHT_MM) {
        scaledHeight = CONTENT_HEIGHT_MM;
        scaledWidth = CONTENT_HEIGHT_MM * imgAspectRatio;
      }
      
      // Create PDF
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      
      // Center horizontally if scaled width is smaller than content width
      const xPos = MARGIN_MM + (CONTENT_WIDTH_MM - scaledWidth) / 2;
      const yPos = MARGIN_MM;
      
      // Handle multi-page if content is taller than one page
      let remainingHeight = scaledHeight;
      let sourceY = 0;
      let pageHeight = CONTENT_HEIGHT_MM;
      
      while (remainingHeight > 0) {
        const heightToAdd = Math.min(remainingHeight, pageHeight);
        const sourceHeight = (heightToAdd / scaledHeight) * imgHeight;
        
        // Create a temporary canvas for this page's portion
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = imgWidth;
        pageCanvas.height = sourceHeight;
        const pageCtx = pageCanvas.getContext("2d");
        pageCtx.drawImage(canvas, 0, sourceY, imgWidth, sourceHeight, 0, 0, imgWidth, sourceHeight);
        
        // Convert to JPEG with compression for smaller file size
        const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.85);
        const pageHeightMM = (sourceHeight / imgHeight) * scaledHeight;
        
        // Add image to PDF page
        pdf.addImage(pageImgData, "JPEG", xPos, yPos, scaledWidth, pageHeightMM);
        
        remainingHeight -= pageHeight;
        sourceY += sourceHeight;
        
        if (remainingHeight > 0) {
          pdf.addPage();
        }
      }
      
      // Compress PDF and check file size
      let pdfBlob = pdf.output("blob");
      let pdfSize = pdfBlob.size;
      let finalPdf = pdf;
      
      // If PDF is larger than 500KB, reduce image quality further
      if (pdfSize > 500 * 1024) {
        // Re-create with lower quality
        const compressedCanvas = await html2canvas(element, {
          scale: 1.2, // Further reduced scale
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          width: element.scrollWidth,
          height: element.scrollHeight,
        });
        
        const compImgWidth = compressedCanvas.width;
        const compImgHeight = compressedCanvas.height;
        const compAspectRatio = compImgWidth / compImgHeight;
        
        // Scale to fit content width (no extra margin on right)
        let compScaledWidth = CONTENT_WIDTH_MM;
        let compScaledHeight = CONTENT_WIDTH_MM / compAspectRatio;
        
        // If height exceeds page height, scale by height instead
        if (compScaledHeight > CONTENT_HEIGHT_MM) {
          compScaledHeight = CONTENT_HEIGHT_MM;
          compScaledWidth = CONTENT_HEIGHT_MM * compAspectRatio;
        }
        
        // Center horizontally if scaled width is smaller than content width
        const compXPos = MARGIN_MM + (CONTENT_WIDTH_MM - compScaledWidth) / 2;
        
        // Create new PDF with compressed images
        const newPdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });
        
        let compRemainingHeight = compScaledHeight;
        let compSourceY = 0;
        
        while (compRemainingHeight > 0) {
          const compHeightToAdd = Math.min(compRemainingHeight, CONTENT_HEIGHT_MM);
          const compSourceHeight = (compHeightToAdd / compScaledHeight) * compImgHeight;
          
          const compPageCanvas = document.createElement("canvas");
          compPageCanvas.width = compImgWidth;
          compPageCanvas.height = compSourceHeight;
          const compPageCtx = compPageCanvas.getContext("2d");
          compPageCtx.drawImage(
            compressedCanvas,
            0,
            compSourceY,
            compImgWidth,
            compSourceHeight,
            0,
            0,
            compImgWidth,
            compSourceHeight
          );
          
          const compPageImgData = compPageCanvas.toDataURL("image/jpeg", 0.65);
          const compPageHeightMM = (compSourceHeight / compImgHeight) * compScaledHeight;
          
          newPdf.addImage(compPageImgData, "JPEG", compXPos, yPos, compScaledWidth, compPageHeightMM);
          
          compRemainingHeight -= CONTENT_HEIGHT_MM;
          compSourceY += compSourceHeight;
          
          if (compRemainingHeight > 0) {
            newPdf.addPage();
          }
        }
        
        finalPdf = newPdf;
        pdfBlob = newPdf.output("blob");
        pdfSize = pdfBlob.size;
      }
      
      // Save PDF
      finalPdf.save(filename);
      
      // Extract Base64
      const dataUri = finalPdf.output("datauristring");
      const base64 = dataUri.split(",")[1];
      
      // Optional: hand it to parent
      onPdfReady?.(base64, dataUri, pdfBlob);
      
      await uploadPdf(base64);
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const uploadPdf = async (pdfBase64) => {
    console.log("datadatadatadata", data);
    const payload = {
      registration_no: "",
      payment_mode: data?.paymentMode || "",
      guest_name: data?.guestName || "",
      nationality: data?.nationality || "",
      email: data?.email || "",
      phone: data?.phone || "",
      source: data?.source || "",
      booking_amount: data?.bookingAmount || "",
      address: data?.address || "",
      visa_number: data?.visaNumber || "",
      visa_issue_date: data?.visaIssueDate || "",
      visa_expiry_date: data?.visaExpiryDate || "",
      dob: data?.dob || "",
      purpose_of_visit: data?.purposeOfVisit || "",
      arrived_from: data?.arrivedFrom || "",
      preferences: data?.preferences || "",
      company_name: data?.companyName || "",
      company_gst: data?.companyGST || "",
      designation: data?.designation || "",
      company_address: data?.companyAddress || "",
      company_email: data?.companyEmail || "",
      company_contact: data?.companyContact || "",
      vehicle_no: data?.vehicleNo || "",
      anniversary: data?.anniversary || "",
      remark: data?.remark || "",
      consent: data?.consent || false,
      signature_data_url: data?.signatureDataUrl || "",
      guest_signature: {
        hotel_id: HomepageData?.booking?.hotelId,
        booking_id: HomepageData?.booking?.bookingId,
        reservation_id: "SFRES_12353_12354",
        signature_base64: data.signatureDataUrl,
        base64_pdf: pdfBase64,
        original_filename: `Registration_card_${HomepageData?.booking?.bookingId}`,
      },
    };

    axios
      .post(`${baseUrl}/web-api/upload-signed-doc`, payload)
      .then(() => {
        navigate(
          `/${HomepageData?.booking?.bookingId?.split("_")?.[1]}${
            HomepageData?.booking?.bookingId?.split("_")?.[2]
          }/${localStorage.getItem("bookingHash")}`
        );
      })
      .catch(() => {});
  };

  const addressParts = [
    HotelData?.hotelInfo?.address,
    // [
    //   HotelData?.hotelInfo?.city,
    //   HotelData?.hotelInfo?.state,
    //   HotelData?.hotelInfo?.country,
    //   HotelData?.hotelInfo?.zipcode,
    // ]
    //   .filter(Boolean)
    //   .join(", "),
  ]
    .filter(Boolean)
    .join(" • ");

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 3 },
      }}
    >
      {/* A4-sized printable card */}
      <Paper
        ref={printAreaRef} // <= NEW: the capture target
        elevation={1}
        sx={{
          width: "calc(210mm - 24mm)",
          minHeight: "calc(297mm - 24mm)",
          mx: "auto",
          paddingX: { xs: 2, md: 3 },
          bgcolor: "background.paper",
          color: "text.secondary",
          boxSizing: "border-box",
          overflow: "visible",
          wordWrap: "break-word",
          overflowWrap: "break-word",
        }}
      >
        {/* --- your entire existing content below is unchanged --- */}
        {/* Header */}
        <Grid container alignItems="center" spacing={2} className="no-break">
          <Grid item xs={3}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {HotelData?.propertyLogoBase64 ? (
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    border: 1,
                    borderColor: "divider",
                    overflow: "hidden",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#fff",
                  }}
                >
                  <Box
                    component="img"
                    src={toDataUrl(HotelData?.propertyLogoBase64) || ""}
                    alt={HotelData?.hotelInfo?.name || "Hotel Logo"}
                    sx={{
                      width: 56,
                      height: 56,
                      objectFit: "contain",
                    }}
                  />
                </Box>
              ) : (
                <Avatar
                  src={HotelData?.propertyLogo || ""}
                  alt={HotelData?.hotelInfo?.name || "Hotel Logo"}
                  variant="rounded"
                  sx={{ width: 56, height: 56, fontWeight: 700 }}
                >
                  {(HotelData?.hotelInfo?.name || "H").slice(0, 1)}
                </Avatar>
              )}
            </Stack>
          </Grid>

          <Grid item xs={6} textAlign="center">
            <Typography
              sx={{ fontWeight: 500, fontSize: 14 }}
              color="text.secondary"
            >
              Registration card
            </Typography>
            <Typography
              sx={{
                fontWeight: 600,
                lineHeight: 1.1,
                color: "text.dark",
                fontSize: 24,
              }}
            >
              {HotelData?.hotelInfo?.name || "Hotel Name"}
            </Typography>
            <Stack
              direction="row"
              justifyContent="center"
              spacing={1}
              flexWrap="wrap"
              sx={{
                mt: 0.5,
                "& .MuiChip-root": {
                  color: "text.dark",
                  wordBreak: "break-word",
                  maxWidth: "100%",
                },
              }}
            >
              {HotelData?.hotelInfo?.contact && (
                <Chip
                  size="small"
                  label={`Phone: ${HotelData?.hotelInfo?.contact}`}
                />
              )}
              {HotelData?.hotelInfo?.emailid && (
                <Chip
                  size="small"
                  label={`Email: ${HotelData?.hotelInfo?.emailid}`}
                />
              )}
              {HotelData?.hotelInfo?.website && (
                <Chip size="small" label={HotelData?.hotelInfo?.website} />
              )}
              {HotelData?.hotelInfo?.tax_identification_number && (
                <Chip
                  size="small"
                  label={`GSTIN: ${HotelData?.hotelInfo?.tax_identification_number}`}
                />
              )}
            </Stack>
          </Grid>

          <Grid item xs={3} textAlign="right">
            <Stack spacing={0.5} alignItems="flex-end">
              <Typography
                sx={{ fontWeight: 500, fontSize: 14 }}
                color="text.secondary"
              >
                Registration no. #
              </Typography>
              <Typography
                sx={{ fontWeight: 800, color: "text.dark", fontSize: 24 }}
              >
                {NV(data?.registrationNo)}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            {addressParts ? (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.dark"
                  sx={{ fontWeight: 500, flexShrink: 0 }}
                >
                  Address :
                </Typography>
                <Typography
                  variant="caption"
                  color="text.dark"
                  sx={{
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {addressParts}
                </Typography>
              </Box>
            ) : null}
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Body */}
        <Table
          size="small"
          sx={{
            border: 1,
            borderColor: "divider",
            tableLayout: "fixed",
            width: "100%",
            "& .MuiTableCell-root": {
              color: "text.dark",
              wordBreak: "break-word",
              overflowWrap: "break-word",
            },
          }}
        >
          <TableBody>
            <SectionRow title="Guest Details" />
            <TableRow>
              <FieldCells label="Guest Name" value={NV(data?.guestName)} />
              <FieldCells label="Date of Birth" value={fmtDate(data?.dob)} />
            </TableRow>
            <TableRow>
              <FieldCells label="Nationality" value={NV(data?.nationality)} />
              <FieldCells label="Phone" value={NV(data?.phone)} />
            </TableRow>
            <TableRow>
              <FieldCells label="Email" value={NV(data?.email)} />
              <FieldCells label="Address" value={NV(data?.address)} />
            </TableRow>

            <SectionRow title="Travel & ID" />
            <TableRow>
              <FieldCells label="Arrived From" value={NV(data?.arrivedFrom)} />
              <FieldCells label="Vehicle No." value={NV(data?.vehicleNo)} />
            </TableRow>
            <TableRow>
              <FieldCells label="Visa Number" value={NV(data?.visaNumber)} />
              <FieldCells
                label="Visa Issue / Expiry"
                value={`${fmtDate(data?.visaIssueDate)}  →  ${fmtDate(
                  data?.visaExpiryDate
                )}`}
              />
            </TableRow>

            <SectionRow title="Stay & Payment" />
            <TableRow>
              <FieldCells
                label="Folio#"
                value={NV(HomepageData?.booking?.bookingId)}
              />
              <FieldCells label="Nights/Adults/Children" value={NV("")} />
            </TableRow>
            <TableRow>
              <FieldCells
                label="Check-in:"
                value={NV(HomepageData?.booking?.bookingCin)}
              />
              <FieldCells
                label="Check-out:"
                value={NV(HomepageData?.booking?.bookingCout)}
              />
            </TableRow>
            <TableRow>
              <FieldCells label="Source" value={NV(data?.source)} />
              <FieldCells label="Payment Mode" value={NV(data?.paymentMode)} />
            </TableRow>
            <TableRow>
              <FieldCells
                label="Booking Amount"
                value={NV(data?.bookingAmount)}
              />
              <FieldCells
                label="Purpose of Visit"
                value={NV(data?.purposeOfVisit)}
              />
            </TableRow>
            <TableRow>
              <FieldCells label="Preferences" value={NV(data?.preferences)} />
              <FieldCells
                label="Anniversary"
                value={fmtDate(data?.anniversary)}
              />
            </TableRow>

            <SectionRow title="Company / Billing Details" />
            <TableRow>
              <FieldCells label="Company Name" value={NV(data?.companyName)} />
              <FieldCells label="Designation" value={NV(data?.designation)} />
            </TableRow>
            <TableRow>
              <FieldCells label="Company GST" value={NV(data?.companyGST)} />
              <FieldCells
                label="Company Contact"
                value={NV(data?.companyContact)}
              />
            </TableRow>
            <TableRow>
              <FieldCells
                label="Company Email"
                value={NV(data?.companyEmail)}
              />
              <FieldCells
                label="Company Address"
                value={NV(data?.companyAddress)}
              />
            </TableRow>

            <SectionRow title="Remarks & Consent" />
            <TableRow>
              <FieldCells label="Remark" value={NV(data?.remark)} />
              <TableCell colSpan={2} sx={{ borderColor: "divider" }} />
            </TableRow>
            <TableRow>
              <TableCell colSpan={4} sx={{ py: 1.5, borderColor: "divider" }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  {data?.consent ? (
                    <CheckCircleOutlineIcon fontSize="small" />
                  ) : (
                    <RadioButtonUncheckedIcon fontSize="small" />
                  )}
                  <Typography variant="body2" color="text.dark">
                    I consent to the hotel's registration, billing, and
                    applicable KYC policies.
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>

            <SectionRow title="Signatures" />
            <TableRow>
              <TableCell
                colSpan={2}
                sx={{ height: 110, borderColor: "divider" }}
              >
                <Typography variant="caption" color="text.secondary">
                  Guest Signature
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    border: "1px dashed",
                    borderColor: "divider",
                    height: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {data?.signatureDataUrl ? (
                    <Box
                      component="img"
                      src={data.signatureDataUrl}
                      alt="Guest Signature"
                      sx={{
                        maxHeight: "100%",
                        maxWidth: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      (Signature will appear here)
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell
                colSpan={2}
                sx={{ verticalAlign: "top", borderColor: "divider" }}
              >
                <Typography variant="caption" color="text.secondary">
                  Front Desk
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    borderTop: 1,
                    borderColor: "divider",
                    height: 80,
                  }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* Footer / T&Cs */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Note: Please check all details are correct. By signing, you agree to
            hotel T&C and policies.
          </Typography>
        </Box>
      </Paper>

      {/* Print CSS (kept; also helps html2canvas pick styles) */}
      <style>
        {`
  @page { size: A4; margin: 12mm; }

  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

    /* Keep the header together, but allow the table to paginate row-by-row */
    .no-break { break-inside: avoid; page-break-inside: avoid; }

    /* Let the table split BETWEEN rows (not inside one) */
    table { break-inside: auto; page-break-inside: auto; }
    tr    { break-inside: avoid; page-break-inside: avoid; }
    td, th{ break-inside: avoid; page-break-inside: avoid; }
  }
`}
      </style>

      <Box
        sx={{
          marginX: "auto",
          width: 210,
          mt: 2,
          displayPrint: "none",
          display: "flex",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Button
          variant="dark-button"
          onClick={() => {
            window.history.back();
          }}
        >
          {"< Modify"}
        </Button>
        <Button
          onClick={handlePrint}
          disabled={isGenerating}
          variant="light-button"
        >
          {isGenerating ? "Generating..." : "Submit"}
        </Button>
      </Box>
    </Box>
  );
}
